<script lang="ts" setup>
import { QMenu } from 'quasar'
import { type Ref, computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useHandleScrollQMenu } from '../../composables/menu/handleScrollQMenu'
import type { UseHandleScroll } from '../../composables/menu/types/UseHandleScroll'
import type { PMenuAnchor } from './types'

interface Props {
  anchor?: PMenuAnchor
  self?: PMenuAnchor
  offset?: number[]
  fit?: boolean
  noTransition?: boolean
  noParentEvent?: boolean
  noFocus?: boolean
  persistent?: boolean
  cy: string
}

const model = defineModel<boolean>()

const props = withDefaults(defineProps<Props>(), {
  offset() {
    return [0, 0]
  },
  fit: false,
  noFocus: false,
  noTransition: false,
  noParentEvent: false,
  persistent: false,
})

const instance = getCurrentInstance()
const qMenuComponent = ref<InstanceType<typeof QMenu>>()
const handleScrollQMenu = ref<UseHandleScroll | null>(null)

const transition = computed<string>(() => {
  return props.noTransition ? '' : 'fade'
})

const updatePosition = (): void => {
  qMenuComponent.value?.updatePosition()
}

onMounted(() => {
  if (!instance) {
    return
  }

  handleScrollQMenu.value = useHandleScrollQMenu(
    qMenuComponent as Ref<QMenu>,
    instance.parent?.vnode.el as HTMLElement,
  )
})

defineExpose({
  updatePosition,
})
</script>

<template>
  <q-menu
    ref="qMenuComponent"
    v-model="model"
    :anchor="anchor"
    :self="self"
    :fit="fit"
    :offset="offset"
    :no-focus="noFocus"
    :persistent="persistent"
    :transition-duration="0"
    :transition-show="transition"
    :no-parent-event="noParentEvent"
    :data-cy="cy"
    no-refocus
    @show="handleScrollQMenu?.subscribe"
    @hide="handleScrollQMenu?.unsubscribe"
  >
    <slot />
  </q-menu>
</template>

<style lang="scss" scoped></style>
